// components/marketing/factoryUi.tsx
'use client';

import React from 'react';

// Factory in ChatGPT's Library style (owner, 2026-09-30: '/chatgptize the whole factory'), shared by
// the bank, the folder view and the three libraries so the sections cannot drift apart. Sizes are
// inline: the app's global clamp beats Tailwind text sizes on div, span and button.

/** The card grid every Factory section uses: 220px minimum, rows further apart than columns
 *  because each card carries its name under the picture (as the module homes' grids). */
export const FACTORY_GRID = 'grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 gap-y-7';

/** A section's own action ("Add persona"): a quiet outlined pill. The page's one white pill is the
 *  header's; these are secondary. */
export const FACTORY_PILL = 'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-[#262626] px-3 text-[#ECECEC] transition-colors hover:bg-white/[0.06] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ECECEC] disabled:opacity-40';

/** A header chip (the quick actions): no outline, as the module homes' view chips. */
export const FACTORY_CHIP = 'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-[#ECECEC] transition-colors hover:bg-white/[0.06] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ECECEC] disabled:opacity-40';

/** A round ghost icon button beside a card's name. */
export const FACTORY_ICON_BUTTON = 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#AFAFAF] transition-colors hover:bg-white/[0.08] hover:text-[#ECECEC] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#ECECEC]';

export const FactorySection = ({ id, title, description, action, first = false, children, ...rest }: {
  id: string;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  first?: boolean;
  children: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, 'title'>) => (
  <section {...rest} className={first ? undefined : 'mt-10'} aria-labelledby={id}>
    <div className="mb-4 flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1">
      <h2 id={id} className="font-semibold text-[#ECECEC]" style={{ fontSize: 16, lineHeight: '24px' }}>{title}</h2>
      {description ? <p className="min-w-0 text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '20px' }}>{description}</p> : null}
      {action ? <span className="ml-auto">{action}</span> : null}
    </div>
    {children}
  </section>
);

/** ChatGPT's Library tile: the picture is the card, the name and one grey line under it, the
 *  card's actions beside the name (shown on hover and on keyboard focus). */
export const FactoryTile = ({ picture, name, meta, actions, openProps, footer }: {
  picture: React.ReactNode;
  name: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  openProps: React.ButtonHTMLAttributes<HTMLButtonElement> & Record<`data-${string}`, string | undefined>;
  footer?: React.ReactNode;
}) => (
  <article className="group min-w-0">
    <button type="button" {...openProps} className="block aspect-[4/3] w-full overflow-hidden rounded-[16px] bg-[#1B1B1B] text-left transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ECECEC]">
      {picture}
    </button>
    <div className="mt-2 flex items-start gap-1 px-1">
      {/* The name opens the item too, as on ChatGPT's tiles; the picture is the keyboard target. */}
      <div className={`min-w-0 flex-1 ${openProps.disabled ? '' : 'cursor-pointer'}`} onClick={openProps.disabled ? undefined : openProps.onClick as React.MouseEventHandler<HTMLDivElement> | undefined}>
        <p className="truncate font-medium text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>{name}</p>
        {meta ? <p className="truncate text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>{meta}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">{actions}</div> : null}
    </div>
    {footer}
  </article>
);

/** A quiet empty-collection line. */
export const FactoryEmpty = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '20px' }}>{children}</p>
);
